import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { socketService } from '../services/socketService';
import { ApplicationData, ReferralData } from '../types/interview';

// Define a queue for actions to be executed upon connection
let onConnectActions: (() => void)[] = [];

// Function to execute all queued actions
export const executeOnConnectActions = () => {
  onConnectActions.forEach(action => action());
  onConnectActions = []; // Clear queue after execution
};

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

interface WebSocketState {
  // Connection state
  connectionStatus: ConnectionStatus;
  reconnectAttempts: number;
  lastConnectionError?: string;
  clientId?: string;

  // Processing states
  isProcessing: boolean;
  processingMessage?: string;

  // Actions
  connect: () => Promise<void>;
  disconnect: () => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setProcessing: (processing: boolean, message?: string) => void;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;
  setConnectionError: (error: string) => void;

  // WebSocket message sending actions
  startInterview: (data: ApplicationData) => void;
  sendUserMessage: (text: string, inputMethod?: string, questionIndex?: number) => void;
  sendSalaryResponse: (salaryInput: string) => void;
  submitBooleanAnswer: (questionIndex: number, answer: boolean) => void;
  submitRating: (rating: number) => void;
  submitReferral: (referralData: ReferralData) => void;
  submitFeedback: (feedback: string) => void;
}

export const useWebSocketStore = create<WebSocketState>()(
  devtools(
    (set, get) => ({
      // Initial state
      connectionStatus: 'disconnected',
      reconnectAttempts: 0,
      isProcessing: false,

      // Connection actions
      connect: async () => {
        try {
          set({ connectionStatus: 'connecting', isProcessing: true, processingMessage: 'Connecting...' });
          await socketService.connect();
          // On successful connection, the status will be updated by the onConnectionEstablished listener
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Connection failed';
          set({
            connectionStatus: 'failed',
            isProcessing: false,
            processingMessage: undefined,
            lastConnectionError: errorMessage
          });
          get().incrementReconnectAttempts();
          throw error;
        }
      },

      disconnect: () => {
        socketService.disconnect();
        set({
          connectionStatus: 'disconnected',
          isProcessing: false,
          processingMessage: undefined
        });
      },

      setConnectionStatus: (status: ConnectionStatus) => {
        set({ connectionStatus: status });
      },

      setProcessing: (processing: boolean, message?: string) => {
        set({ isProcessing: processing, processingMessage: message });
      },

      incrementReconnectAttempts: () => {
        set((state) => ({
          reconnectAttempts: state.reconnectAttempts + 1
        }));
      },

      resetReconnectAttempts: () => {
        set({ reconnectAttempts: 0 });
      },

      setConnectionError: (error: string) => {
        set({ lastConnectionError: error });
      },

      // WebSocket message sending actions
      startInterview: (data: ApplicationData) => {
        console.log('WebSocket Store: Queuing start interview action');
        const action = () => {
          console.log('WebSocket Store: Executing queued start interview action:', data);
          set({ isProcessing: true, processingMessage: 'Starting interview...' });
          socketService.startInterviewWithId(data);
        };

        if (get().connectionStatus === 'connected') {
          action();
        } else {
          onConnectActions.push(action);
          console.log('WebSocket Store: Connection not ready, action queued.');
          // Attempt to connect if not already connecting or connected
          if (get().connectionStatus !== 'connecting') {
            // Fire-and-forget connect attempt must ALWAYS handle rejection to avoid
            // global `unhandledrejection` noise in Sentry.
            void get()
              .connect()
              .catch((error) => {
                console.error("WebSocket Store: Auto-connect attempt failed:", error);
              });
          }
        }
      },

      sendUserMessage: (text: string, inputMethod?: string, questionIndex?: number) => {
        console.log('WebSocket Store: Sending user message:', text.substring(0, 50));
        console.log('WebSocket Store: Connection status:', get().connectionStatus);
        set({ isProcessing: true, processingMessage: 'Sending response...' });

        try {
          socketService.sendUserMessage(text, inputMethod, questionIndex);
          console.log('WebSocket Store: Message sent successfully');

        } catch (error) {
          console.error('WebSocket Store: Error sending message:', error);
          set({ isProcessing: false, processingMessage: undefined });
        }
      },

      sendSalaryResponse: (salaryInput: string) => {
        console.log('WebSocket Store: Sending salary response:', salaryInput);
        set({ isProcessing: true, processingMessage: 'Processing salary information...' });
        socketService.sendSalaryResponse(salaryInput);
      },

      submitBooleanAnswer: (questionIndex: number, answer: boolean) => {
        console.log('WebSocket Store: Submitting boolean answer:', { questionIndex, answer });
        set({ isProcessing: true, processingMessage: 'Submitting answer...' });
        socketService.submitBooleanAnswer(questionIndex, answer);
      },

      submitRating: (rating: number) => {
        console.log('WebSocket Store: Submitting rating:', rating);
        set({ isProcessing: true, processingMessage: 'Submitting rating...' });
        socketService.submitRating(rating);
      },

      submitReferral: (referralData: ReferralData) => {
        console.log('WebSocket Store: Submitting referral:', referralData);
        set({ isProcessing: true, processingMessage: 'Submitting referral...' });
        socketService.submitReferral(referralData);
      },

      submitFeedback: (feedback: string) => {
        console.log('WebSocket Store: Submitting feedback:', feedback.substring(0, 50));
        set({ isProcessing: true, processingMessage: 'Submitting feedback...' });
        socketService.submitFeedback(feedback);
      },
    }),
    {
      name: 'websocket-store',
    }
  )
);
